import {
  AlertCircle,
  Bell,
  BellOff,
  CheckCircle,
  Info,
  Smartphone,
  TestTube,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { usePushNotifications } from '@/hooks/usePushNotifications';

export function NotificationSettings() {
  const {
    isSupported,
    isSubscribed,
    isLoading,
    permission,
    subscribe,
    unsubscribe,
    sendTestNotification,
  } = usePushNotifications();

  const getPermissionBadge = () => {
    switch (permission) {
      case 'granted':
        return (
          <Badge className="gap-1" variant="default">
            <CheckCircle className="h-3 w-3" />
            Permitida
          </Badge>
        );
      case 'denied':
        return (
          <Badge className="gap-1" variant="destructive">
            <AlertCircle className="h-3 w-3" />
            Negada
          </Badge>
        );
      default:
        return (
          <Badge className="gap-1" variant="secondary">
            <Info className="h-3 w-3" />
            Não solicitada
          </Badge>
        );
    }
  };

  const getSupportMessage = () => {
    if (!isSupported) {
      return {
        icon: <AlertCircle className="h-5 w-5 text-yellow-500" />,
        message: 'Seu navegador não suporta notificações push.',
        description:
          'Para receber notificações, use um navegador moderno como Chrome, Firefox ou Safari.',
      };
    }

    if (permission === 'denied') {
      return {
        icon: <BellOff className="h-5 w-5 text-red-500" />,
        message: 'Notificações bloqueadas',
        description:
          'Você bloqueou as notificações. Para ativá-las, clique no ícone de cadeado na barra de endereço e permita notificações.',
      };
    }

    if (isSubscribed) {
      return {
        icon: <Bell className="h-5 w-5 text-green-500" />,
        message: 'Notificações ativas',
        description:
          'Você receberá notificações sobre suas metas financeiras, conquistas e lembretes importantes.',
      };
    }

    return {
      icon: <Smartphone className="h-5 w-5 text-blue-500" />,
      message: 'Ativar notificações',
      description:
        'Receba lembretes sobre suas metas, conquistas desbloqueadas e dicas financeiras personalizadas.',
    };
  };

  const supportInfo = getSupportMessage();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Notificações Push
        </CardTitle>
        <CardDescription>
          Configure notificações para receber lembretes e acompanhar seu
          progresso
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Status atual */}
        <div className="flex items-center justify-between rounded-lg border p-4">
          <div className="flex items-center gap-3">
            {supportInfo.icon}
            <div>
              <h4 className="font-medium">{supportInfo.message}</h4>
              <p className="text-muted-foreground text-sm">
                {supportInfo.description}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">{getPermissionBadge()}</div>
        </div>

        {/* Controles principais */}
        {isSupported && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-base" htmlFor="notifications-toggle">
                  Ativar Notificações
                </Label>
                <div className="text-muted-foreground text-sm">
                  Receba notificações sobre suas atividades financeiras
                </div>
              </div>
              <Switch
                checked={isSubscribed}
                disabled={isLoading || permission === 'denied'}
                id="notifications-toggle"
                onCheckedChange={async (checked) => {
                  if (checked) {
                    await subscribe();
                  } else {
                    await unsubscribe();
                  }
                }}
              />
            </div>

            {/* Botões de ação */}
            <div className="flex gap-2">
              {isSubscribed && (
                <Button
                  className="gap-2"
                  disabled={isLoading}
                  onClick={sendTestNotification}
                  size="sm"
                  variant="outline"
                >
                  <TestTube className="h-4 w-4" />
                  Teste
                </Button>
              )}

              {permission === 'denied' && (
                <Button
                  onClick={() => {
                    // Guide user to manually enable notifications
                    alert(
                      'Para ativar notificações:\n1. Clique no ícone de cadeado na barra de endereços\n2. Selecione "Permitir" para notificações\n3. Recarregue a página'
                    );
                  }}
                  size="sm"
                  variant="outline"
                >
                  Como ativar?
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Tipos de notificação */}
        {isSubscribed && (
          <div className="space-y-3">
            <h4 className="font-medium">Tipos de Notificação</h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label
                  className="flex flex-col gap-1"
                  htmlFor="goal-notifications"
                >
                  <span>Progresso das Metas</span>
                  <span className="font-normal text-muted-foreground text-sm">
                    Quando você atingir marcos importantes nas suas metas
                  </span>
                </Label>
                <Switch defaultChecked id="goal-notifications" />
              </div>

              <div className="flex items-center justify-between">
                <Label
                  className="flex flex-col gap-1"
                  htmlFor="achievement-notifications"
                >
                  <span>Conquistas Desbloqueadas</span>
                  <span className="font-normal text-muted-foreground text-sm">
                    Quando você conquistar novos marcos de economia
                  </span>
                </Label>
                <Switch defaultChecked id="achievement-notifications" />
              </div>

              <div className="flex items-center justify-between">
                <Label
                  className="flex flex-col gap-1"
                  htmlFor="reminder-notifications"
                >
                  <span>Lembretes Diários</span>
                  <span className="font-normal text-muted-foreground text-sm">
                    Lembretes para registrar gastos e acompanhar progresso
                  </span>
                </Label>
                <Switch defaultChecked id="reminder-notifications" />
              </div>

              <div className="flex items-center justify-between">
                <Label
                  className="flex flex-col gap-1"
                  htmlFor="tip-notifications"
                >
                  <span>Dicas Financeiras</span>
                  <span className="font-normal text-muted-foreground text-sm">
                    Dicas personalizadas baseadas nos seus hábitos
                  </span>
                </Label>
                <Switch id="tip-notifications" />
              </div>
            </div>
          </div>
        )}

        {/* Informações técnicas (para debug) */}
        {process.env.NODE_ENV === 'development' && (
          <details className="text-muted-foreground text-xs">
            <summary className="cursor-pointer">Debug Info</summary>
            <pre className="mt-2 rounded bg-muted p-2 text-xs">
              {JSON.stringify(
                {
                  isSupported,
                  isSubscribed,
                  permission,
                  userAgent: navigator.userAgent,
                },
                null,
                2
              )}
            </pre>
          </details>
        )}
      </CardContent>
    </Card>
  );
}
